<!-- Shared "API Call" config block for the build form's Dropdown / Slider &
     Input Box / File Upload sections — reused via ngTemplateOutlet right
     after each section's "Target chart(s)/input(s)" picker. When enabled, a
     value change on THIS control fires the configured request instead of the
     built-in generate/recompute call, and the response is applied to the
     targets above exactly like a normal recompute result — see
     resolveInputComputeCall / InputApiCallConfig. -->
<ng-template #apiCallConfigBlock let-kind="kind">
  <div class="control-group control-group-full dvm-api-call-config">
    <label class="dvm-api-call-toggle">
      <input type="checkbox" [(ngModel)]="buildApiCallConfig.enabled" />
      <span>Use a custom API call for this control</span>
    </label>

    <ng-container *ngIf="buildApiCallConfig.enabled">
      <div class="dvm-api-call-row">
        <select class="dvm-api-call-method" [(ngModel)]="buildApiCallConfig.method">
          <option value="GET">GET</option>
          <option value="POST">POST</option>
          <option value="PUT">PUT</option>
          <option value="PATCH">PATCH</option>
        </select>
        <input type="text" class="dvm-api-call-url" placeholder="https://api.example.com/endpoint" [(ngModel)]="buildApiCallConfig.url" />
      </div>

      <label>When this control changes</label>
      <!-- ng-select, not a native <select>: a native popup is sized to its
           longest option and spilled past the panel. The panel keeps the
           control's width; the hover tooltip shows a clipped label. -->
      <ng-select
        #apiModeSel
        class="ss-select dvm-api-call-mode"
        [class.is-open]="!!apiModeSel.isOpen()"
        appendTo="body"
        ngClass="ss-dropdown-panel"
        [items]="apiCallModeOptions"
        bindLabel="label"
        bindValue="value"
        [clearable]="false"
        [searchable]="false"
        [(ngModel)]="buildApiCallConfig.mode"
      ></ng-select>

      <ng-container *ngIf="buildApiCallConfig.mode === 'parallel'">
        <label>Targets this API answers for</label>
        <ng-select
          #apiTargetSel
          class="ss-select"
          [class.is-open]="!!apiTargetSel.isOpen()"
          appendTo="body"
          ngClass="ss-dropdown-panel"
          [items]="apiCallTargetOptions"
          bindLabel="label"
          bindValue="value"
          [multiple]="true"
          [closeOnSelect]="false"
          [hideSelected]="false"
          [clearable]="true"
          [searchable]="true"
          placeholder="Pick the target(s) your microservice returns"
          [(ngModel)]="buildApiCallConfig.targets"
        ></ng-select>
        <p class="dvm-pt-hint">
          Both requests fire at the same time. The target(s) picked here are answered by your
          API; every other target above still comes from the normal compute API, and the two
          responses are merged. Leave this empty to fire your API as a pure side effect while
          compute keeps serving all targets.
        </p>
        <small class="control-hint" *ngIf="!apiCallTargetOptions.length">
          Add a chart, KPI or input to the dashboard first so it can be assigned here.
        </small>
      </ng-container>

      <ng-container *ngIf="buildApiCallConfig.method !== 'GET'">
        <label>Request body</label>
        <textarea class="dvm-api-call-body" rows="6" [(ngModel)]="buildApiCallConfig.bodyTemplate" spellcheck="false"></textarea>
        <p class="dvm-pt-hint">
          Tokens: <code>{{ '{value}' }}</code> (current value), <code>{{ '{componentId}' }}</code>
          <ng-container *ngIf="kind === 'dropdown'">, <code>{{ '{field}' }}</code></ng-container>
          <ng-container *ngIf="kind === 'upload'">, <code>{{ '{fileName}' }}</code>, <code>{{ '{fileType}' }}</code></ng-container>
          — a token as the WHOLE value of a JSON string (e.g. <code>"{{ '{value}' }}"</code>) keeps its native type; embedded in a longer string it's stringified.
        </p>
      </ng-container>

      <p class="dvm-pt-hint">
        Response is expected in the same shape the built-in compute call already returns:
        <code>{{ '{ "results": [{ "componentId": "...", "result": {...} }] }' }}</code>
      </p>
    </ng-container>
  </div>
</ng-template>

<section
  class="dvm-layout-builder-workspace"
  [class.steps-panel-collapsed]="stepsPanelCollapsed"
  [class.preview-panel-collapsed]="previewPanelCollapsed"
  [class.data-panel-collapsed]="dataPanelCollapsed"
  [style.--steps-pane-width.px]="stepsPaneWidth"
  [style.--data-pane-width.px]="dataPaneWidth"
  [style.grid-template-columns]="paneGridTemplate"
>
  <!-- ══════════════════ Build panel (one <app-*-step> per widget family) ══════════════════ -->
  <aside class="dvm-layout-steps-panel wf-section wf-steps-section" [class.is-collapsed]="stepsPanelCollapsed" [style.grid-column]="paneGridColumn('steps')" (dragover)="onPaneDragOver($event)" (drop)="onPaneDrop('steps', $event)">
      <header class="wf-section-header wf-steps-head pane-drag-handle" draggable="true" (dragstart)="onPaneDragStart('steps', $event)" (dragend)="onPaneDragEnd()" matTooltip="Drag to reorder panel">
        <div><span class="wf-section-kicker">Build</span><h3>{{ buildPanelTitle }} <span class="wf-step-count" *ngIf="buildPanelCount !== null">{{ buildPanelCount }}</span></h3></div>
        <div class="wf-header-actions">
          <button *ngIf="isWaterfallLayout" type="button" class="add-step-button" mat-stroked-button (click)="addWaterfallStep()"><mat-icon>add</mat-icon> Add step</button>
          <button
            type="button"
            class="panel-collapse-button"
            mat-icon-button
            (click)="stepsPanelCollapsed = !stepsPanelCollapsed"
            [matTooltip]="stepsPanelCollapsed ? 'Open bridge steps' : 'Collapse bridge steps'"
            [attr.aria-label]="stepsPanelCollapsed ? 'Open bridge steps panel' : 'Collapse bridge steps panel'"
          ><mat-icon>{{ paneCollapseIcon('steps') }}</mat-icon></button>
        </div>
      </header>
      <div class="panel-content steps-panel-content">

      <div class="bridge-chart-settings">
        <div class="bridge-settings-heading"><mat-icon>tune</mat-icon><span>{{ layoutSectionTitle('config', 'Chart configuration') }}</span></div>

        <!-- LAYER 1 — Chart Catalogue: Category → Type. The Category picker shows
             only when a catalogue is configured; it filters the Type list below. -->
        <div class="control-group" *ngIf="hasChartCatalogue">
          <label for="bridgeChartCategory">Chart category</label>
          <ng-select
            labelForId="bridgeChartCategory"
            #ssBridgeCategory
            class="ss-select"
            [class.is-open]="!!ssBridgeCategory.isOpen()"
            appendTo="body"
            ngClass="ss-dropdown-panel"
            [items]="chartCategories"
            bindLabel="label"
            bindValue="id"
            [clearable]="true"
            [ngModel]="selectedChartCategory"
            (ngModelChange)="selectedChartCategory = $event ?? ''; onChartCategoryChange()"
          ></ng-select>
        </div>

        <div class="control-group">
          <label for="bridgeLayoutType">{{ hasChartCatalogue ? 'Chart type' : 'Chart layout' }}</label>
          <ng-select
            labelForId="bridgeLayoutType"
            #ssBridgeLayout
            class="ss-select"
            [class.is-open]="!!ssBridgeLayout.isOpen()"
            appendTo="body"
            ngClass="ss-dropdown-panel"
            [items]="hasChartCatalogue ? typesForSelectedCategory : layoutTypes"
            bindLabel="label"
            bindValue="value"
            [clearable]="true"
            [ngModel]="layoutTypePickerValue"
            (ngModelChange)="selectedLayoutType = $event ?? ''; onLayoutTypeChange()"
          ></ng-select>
        </div>
        <!-- Title moved into its own 'Title' accordion (Bar & Waterfall layouts).
             For the remaining layouts (kpi/drill-down/…) keep the inline Title
             field here; config-driven — a layout's config section listing
             `title` in its fields shows it, omitting it hides it. -->
        <!-- Bar / Line & Area / Drill-Down surface their Title via a 'Title' format
             accordion, so exclude the inline field for them. -->
        <div class="control-group" *ngIf="!isBarLayout && !isLineAreaLayout && !(isTableMatrixLayout && isTmDrilldown) && fieldEnabled('config', 'title')">
          <label for="bridgeTitle">Title</label>
          <input id="bridgeTitle" type="text" [(ngModel)]="title" placeholder="e.g. Gross Revenue to Net Profit" />
        </div>
        <!-- <div class="selected-data-summary">
          <mat-icon>database</mat-icon>
          <span>{{ selectedDatasets.length ? selectedDatasets.length + ' table(s) selected' : 'Drag a field from the Data pane to select a table' }}</span>
        </div> -->
      </div>

      <app-dvm-relation-hops
        *ngIf="activeRelationHops as relationConfig"
        [config]="relationConfig"
        [value]="relationHops"
        (valueChange)="relationHops = $event"
      ></app-dvm-relation-hops>

      <!-- Waterfall (bridge steps) build form → components/waterfall-step -->
      <app-waterfall-step [datasetPicker]="datasetPicker" [referenceList]="referenceList" [chartFormatAccordion]="chartFormatAccordion" [apiCallConfigBlock]="apiCallConfigBlock"></app-waterfall-step>

      <!-- Table & Matrix build form → components/table-matrix-step -->
      <app-table-matrix-step [datasetPicker]="datasetPicker" [referenceList]="referenceList" [chartFormatAccordion]="chartFormatAccordion" [apiCallConfigBlock]="apiCallConfigBlock"></app-table-matrix-step>

      <!-- Nested table: levels + per-level columns. -->
      <app-nested-table-step [datasetPicker]="datasetPicker" [referenceList]="referenceList" [chartFormatAccordion]="chartFormatAccordion" [apiCallConfigBlock]="apiCallConfigBlock"></app-nested-table-step>

    <!-- Drill-down build form → components/drill-down-step -->
    <app-drill-down-step [datasetPicker]="datasetPicker" [referenceList]="referenceList" [chartFormatAccordion]="chartFormatAccordion" [apiCallConfigBlock]="apiCallConfigBlock"></app-drill-down-step>

    <!-- Shared reference-list template: one card per typed reference, scoped to
         a specific WATERFALL bar (`step` set) or a single formula (`step`
         null) — used by the Input-control target formulas below AND by a
         chart's OWN formula field (KPI / horizontal bar), keyed by that
         chart's own componentId rather than a target id. Declared here, above
         every section that uses it, so it's in scope for all of them. -->
    <!-- Dataset picker + status — one definition, used by every chart section. -->
    <ng-template #datasetPicker>
      <div class="control-group" *ngIf="fieldEnabled('config', 'dataset')">
        <label>{{ fieldLabel('config', 'dataset', 'Dataset') }}</label>
        <ng-select
          #ssSharedDataset
          class="ss-select"
          [class.is-open]="!!ssSharedDataset.isOpen()"
          appendTo="body"
          ngClass="ss-dropdown-panel"
          [items]="entityOptions"
          [loading]="entitiesLoading"
          [clearable]="true"
          [searchable]="true"
          [multiple]="isNestedTableLayout"
          [closeOnSelect]="!isNestedTableLayout"
          [placeholder]="isNestedTableLayout ? 'Select one or more datasets' : 'Pick the data table to render'"
          [ngModel]="isNestedTableLayout ? selectedDatasets : (drillDataset || null)"
          (ngModelChange)="onSelectedDatasetsChange(isNestedTableLayout ? ($event || []) : ($event ? [$event] : []))"
        ></ng-select>
        <small class="control-hint" *ngIf="datasetStatus.kind === 'hint'">{{ datasetStatus.text }}</small>
        <small class="control-warning" *ngIf="datasetStatus.kind === 'error'">
          {{ datasetStatus.text }}
          <button type="button" class="target-reference-add" *ngIf="datasetStatus.canRetry" (click)="reloadDatasets()">Retry</button>
        </small>
      </div>
    </ng-template>

    <ng-template #referenceList let-id="id" let-step="step">
      <div class="target-reference-list">
        <div class="target-reference-card" *ngFor="let ref of targetReferences(id, step)">
          <div class="target-reference-card-header">
            <span>Reference</span>
            <button type="button" class="target-reference-remove" (click)="removeTargetReference(id, ref)" title="Remove reference">&times;</button>
          </div>
          <div class="target-reference-grid">
            <div class="control-group">
              <label>Token</label>
              <input type="text" [(ngModel)]="ref.token" placeholder="e.g. DN Price" />
            </div>
            <div class="control-group">
              <label>Input title</label>
              <input type="text" [(ngModel)]="ref.inputTitle" placeholder="e.g. Price Inflation %" />
            </div>
            <div class="control-group">
              <label>Target label</label>
              <input type="text" [(ngModel)]="ref.targetLabel" placeholder="e.g. Product Margins" />
            </div>
            <div class="control-group">
              <label>Row label</label>
              <input type="text" [(ngModel)]="ref.rowLabel" placeholder="e.g. DN Price" />
            </div>
          </div>
          <small class="control-warning" *ngIf="isDuplicateReferenceToken(id, ref, step)">
            Another reference already uses token "{{ ref.token.trim() }}" — only one is sent. Give each a unique token.
          </small>
        </div>
        <button type="button" class="target-reference-add" (click)="addTargetReference(id, step)">+ Add reference</button>
      </div>
    </ng-template>

    <!-- Horizontal bar build form → components/horizontal-bar-step -->
    <app-horizontal-bar-step [datasetPicker]="datasetPicker" [referenceList]="referenceList" [chartFormatAccordion]="chartFormatAccordion" [apiCallConfigBlock]="apiCallConfigBlock"></app-horizontal-bar-step>

    <!-- KPI card build form → components/kpi-step -->
    <app-kpi-step [datasetPicker]="datasetPicker" [referenceList]="referenceList" [chartFormatAccordion]="chartFormatAccordion"></app-kpi-step>

    <!-- Bar & column build form → components/bar-column-step -->
    <app-bar-column-step [datasetPicker]="datasetPicker" [referenceList]="referenceList" [chartFormatAccordion]="chartFormatAccordion" [apiCallConfigBlock]="apiCallConfigBlock"></app-bar-column-step>

    <!-- Line & area build form → components/line-area-step -->
    <app-line-area-step [datasetPicker]="datasetPicker" [referenceList]="referenceList" [chartFormatAccordion]="chartFormatAccordion" [apiCallConfigBlock]="apiCallConfigBlock"></app-line-area-step>

    <!-- Scatter & bubble build form → components/scatter-bubble-step -->
    <app-scatter-bubble-step [datasetPicker]="datasetPicker" [referenceList]="referenceList" [chartFormatAccordion]="chartFormatAccordion" [apiCallConfigBlock]="apiCallConfigBlock"></app-scatter-bubble-step>

    <!-- Pie & composition build form → components/pie-composition-step -->
    <app-pie-composition-step [datasetPicker]="datasetPicker" [referenceList]="referenceList" [chartFormatAccordion]="chartFormatAccordion" [apiCallConfigBlock]="apiCallConfigBlock"></app-pie-composition-step>

    <!-- Chart format options — SHARED accordion template (rendered for Bar AND
         Waterfall). Config-driven: formatControls filtered to the ACTIVE layout
         type + grouped by `group`; the capabilityRegistry's `sections` list gates
         which accordions appear. Values fold into the tile on Generate. -->
    <ng-template #chartFormatAccordion>
      <div class="bar-options-accordion" *ngIf="barFormatGroups.length">
        <div class="dvm-format-group" *ngFor="let group of barFormatGroups" [class.is-open]="isBarFormatGroupOpen(group.name)">
          <button type="button" class="dvm-format-group-head" (click)="toggleBarFormatGroup(group.name)">
            <span>{{ group.name }}</span>
            <mat-icon>{{ isBarFormatGroupOpen(group.name) ? 'expand_less' : 'expand_more' }}</mat-icon>
          </button>

          <div class="dvm-format-group-body" *ngIf="isBarFormatGroupOpen(group.name)">
            <ng-container *ngFor="let control of group.controls">
            <div class="dvm-format-row" [class.dvm-format-row-half]="control.width === 'half'" *ngIf="formatControlVisible(control, barFormat)">
              <label>{{ control.label }}</label>

              <!-- CRUD endpoints: each gets its OWN request-body editor, right
                   beside its label, so every URL can post a different contract. -->
              <button
                type="button"
                class="dvm-pt-open-btn"
                *ngIf="control.requestBodyEditor"
                [class.is-set]="hasPayloadTemplate(barFormat, control.key)"
                [matTooltip]="'Configure the request body posted to this endpoint'"
                (click)="openPayloadTemplateModalForBar(control)"
              >{{ hasPayloadTemplate(barFormat, control.key) ? 'Body ✓' : 'Body' }}</button>

              <input
                *ngIf="control.control === 'text' || control.control === 'row-filter'"
                type="text"
                [ngModel]="barFormat[control.key]"
                (ngModelChange)="barFormat[control.key] = $event; onBarFormatChange()"
                [placeholder]="control.placeholder || control.label"
              />

              <input
                *ngIf="control.control === 'number'"
                type="number"
                [min]="control.min ?? null"
                [max]="control.max ?? null"
                [step]="control.step ?? 1"
                [ngModel]="barFormat[control.key]"
                (ngModelChange)="barFormat[control.key] = $event; onBarFormatChange()"
              />

              <input
                *ngIf="control.control === 'color'"
                type="color"
                [ngModel]="barFormat[control.key]"
                (ngModelChange)="barFormat[control.key] = $event; onBarFormatChange()"
              />

              <label *ngIf="control.control === 'toggle'" class="dvm-format-toggle">
                <input
                  type="checkbox"
                  [ngModel]="barFormat[control.key]"
                  (ngModelChange)="barFormat[control.key] = $event; onBarFormatChange()"
                />
              </label>

              <ng-select
                *ngIf="control.control === 'select'"
                class="ss-select"
                appendTo="body"
                ngClass="ss-dropdown-panel"
                [items]="formatControlOptions(control)"
                bindLabel="label"
                bindValue="value"
                [clearable]="true"
                [ngModel]="barFormat[control.key]"
                (ngModelChange)="barFormat[control.key] = $event ?? ''; onBarFormatChange()"
              ></ng-select>

              <ng-select
                *ngIf="control.control === 'column-select'"
                class="ss-select"
                appendTo="body"
                ngClass="ss-dropdown-panel"
                [items]="formatControlOptions(control)"
                bindLabel="label"
                bindValue="value"
                [clearable]="true"
                [ngModel]="barFormat[control.key]"
                (ngModelChange)="barFormat[control.key] = $event || ''; onBarFormatChange()"
              ></ng-select>
            </div>
            </ng-container>
          </div>
        </div>
      </div>
    </ng-template>

    <!-- Dropdown build form → components/dropdown-step -->
    <app-dropdown-step [datasetPicker]="datasetPicker" [referenceList]="referenceList" [chartFormatAccordion]="chartFormatAccordion" [apiCallConfigBlock]="apiCallConfigBlock"></app-dropdown-step>

    <!-- File upload build form → components/file-upload-step -->
    <app-file-upload-step [datasetPicker]="datasetPicker" [referenceList]="referenceList" [chartFormatAccordion]="chartFormatAccordion" [apiCallConfigBlock]="apiCallConfigBlock"></app-file-upload-step>

    <!-- Popup build form → components/popup-step -->
    <app-popup-step [datasetPicker]="datasetPicker" [referenceList]="referenceList" [chartFormatAccordion]="chartFormatAccordion" [apiCallConfigBlock]="apiCallConfigBlock"></app-popup-step>

    <!-- Input box / slider build form → components/input-control-step -->
    <app-input-control-step [datasetPicker]="datasetPicker" [referenceList]="referenceList" [chartFormatAccordion]="chartFormatAccordion" [apiCallConfigBlock]="apiCallConfigBlock"></app-input-control-step>

    <!-- Chart interactions: besides filterKey/filterListeners, a CHART can name
         the component(s) it interacts with. Persisted as payload.targets (same
         shape the input controls use, minus per-target formulas) — also
         editable later from the tile header's link icon. OPTIONAL, so it lives
         in a collapsed accordion (steps/axes stay always visible — those are
         required); the badge shows how many targets are wired. -->
    <div class="tm-nav-config" *ngIf="isChartTargetLayout">
      <div class="tm-nav-head" (click)="interactionsSectionOpen = !interactionsSectionOpen">
        <mat-icon>{{ interactionsSectionOpen ? 'expand_more' : 'chevron_right' }}</mat-icon>
        <span>{{ layoutSectionTitle('interactions', 'Chart interactions') }}</span>
        <span class="tm-nav-badge" *ngIf="selectedTargetCharts.length">{{ selectedTargetCharts.length }}</span>
      </div>
      <div class="tm-nav-body" *ngIf="interactionsSectionOpen">
        <small class="control-hint">
          {{ layoutSectionHint('interactions', 'Select the chart(s)/input(s) this chart interacts with — its interactions are sent to these targets.') }}
        </small>
        <div class="control-group control-group-full">
          <label>{{ fieldLabel('interactions', 'targetCharts', 'Target chart(s) / input(s)') }}</label>
          <ng-select
            #ssChartTargets
            class="ss-select ss-select--multi ss-select--full"
            [class.is-open]="!!ssChartTargets.isOpen()"
            appendTo="body"
            ngClass="ss-dropdown-panel"
            [items]="targetCharts"
            bindLabel="label"
            bindValue="value"
            [multiple]="true"
            [closeOnSelect]="false"
            [hideSelected]="false"
            [clearable]="true"
            [searchable]="true"
            placeholder="Select target chart(s)/input(s)"
            [(ngModel)]="selectedTargetCharts"
          ></ng-select>
          <small class="control-hint" *ngIf="!targetCharts.length">Add more charts or inputs to the dashboard first — they'll appear here as targets.</small>
        </div>
      </div>
    </div>

    <!-- Drill-Down: its format accordions (Formatting / Columns / Drill-Down)
         render HERE — below the Hierarchy dimensions / Measures / Chart-
         interactions block — so the required hierarchy mapping stays at the top
         and the optional formatting sits after the interactions accordion. -->
    <ng-container *ngIf="isTableMatrixLayout && isTmDrilldown" [ngTemplateOutlet]="chartFormatAccordion"></ng-container>

    <div class="wf-generate-row">
      <!-- The span carries the tooltip: a disabled <button> swallows pointer
           events, so a title on the button itself would never show. -->
      <span class="wf-generate-button-wrap" [title]="generateBlockerText">
        <button type="button" class="primary-action" [disabled]="!canGenerate" (click)="generateConfiguration()">
          {{ isGenerating ? (editingTileId ? 'Updating...' : 'Generating...') : (editingTileId ? 'Update Configuration' : 'Generate Configuration') }}
        </button>
      </span>
      <!-- Say WHAT is missing — the greyed-out button alone gives the user no
           way to work out which required field is still empty. -->
      <div class="message wf-generate-missing" *ngIf="!canGenerate && !isGenerating && generateBlockers.length">
        <strong>Still needed:</strong>
        <ul>
          <li *ngFor="let item of generateBlockers">{{ item }}</li>
        </ul>
      </div>
      <div class="message warning-message" *ngIf="generateError">{{ generateError }}</div>
    </div>
    </div>
  </aside>

  <div class="pane-resizer steps-pane-resizer" [class.is-disabled]="isDividerDisabled(0)" (mousedown)="startDividerResize(0, $event)" aria-hidden="true"></div>

  <!-- ══════════════════ RIGHT: dynamic preview canvas ══════════════════ -->
  <!-- ══════════════════ RIGHT: dashboard preview canvas ══════════════════ -->
  <div
    class="dvm-layout-preview-panel"
    [class.is-collapsed]="previewPanelCollapsed"
    [class.is-fullscreen]="isDashboardFullscreen"
    [style.grid-column]="paneGridColumn('preview')"
    (dragover)="onPaneDragOver($event)"
    (drop)="onPaneDrop('preview', $event)"
  >
    <button
      *ngIf="previewPanelCollapsed"
      type="button"
      class="collapsed-preview-reopen"
      mat-icon-button
      (mousedown)="$event.stopPropagation()"
      (click)="$event.stopPropagation(); previewPanelCollapsed = false"
      matTooltip="Open dashboard"
      aria-label="Open dashboard panel"
    ><mat-icon>{{ paneCollapseIcon('preview') }}</mat-icon></button>
    <div class="wf-preview-header">
      <span *ngIf="!isDashboardFullscreen" class="pane-drag-handle preview-drag-grip" draggable="true" (dragstart)="onPaneDragStart('preview', $event)" (dragend)="onPaneDragEnd()" matTooltip="Drag to reorder panel"><mat-icon>drag_indicator</mat-icon></span>
      <div class="dvm-dashboard-titlebar">
        <span class="wf-section-kicker">Dashboard</span>
        <input class="dvm-dashboard-name" type="text" [(ngModel)]="dashboardName" placeholder="Untitled dashboard" aria-label="Dashboard name" />
      </div>
      <span class="wf-step-count dvm-tile-count">{{ dashboardTiles.length }} chart{{ dashboardTiles.length === 1 ? '' : 's' }}</span>

      <div class="dvm-preview-actions">
        <!-- Full-screen preview: a single Back button to return to the builder. -->
        <button *ngIf="isDashboardFullscreen" type="button" class="add-step-button dvm-back-button" mat-flat-button color="primary" (click)="toggleDashboardFullscreen()" matTooltip="Back to builder">
          <mat-icon>arrow_back</mat-icon> Back
        </button>
        <button *ngIf="isDashboardFullscreen" type="button" class="add-step-button" mat-stroked-button [class.is-active]="previewEditLayout" (click)="previewEditLayout = !previewEditLayout" [matTooltip]="previewEditLayout ? 'Done editing layout' : 'Rearrange / resize the tiles'">
          <mat-icon>{{ previewEditLayout ? 'done' : 'dashboard_customize' }}</mat-icon> {{ previewEditLayout ? 'Done' : 'Edit Layout' }}
        </button>
        <!-- <button *ngIf="isDashboardFullscreen" type="button" class="add-step-button" mat-stroked-button [disabled]="!canSaveDashboard" (click)="saveDashboard()" matTooltip="Save this dashboard draft">
          <mat-icon>save</mat-icon> {{ isSavingDashboard ? 'Saving…' : 'Save' }}
        </button> -->
        <button *ngIf="isDashboardFullscreen" type="button" class="add-step-button dvm-publish-button" mat-flat-button color="primary" [disabled]="!canPublish" (click)="publishDashboard()" [matTooltip]="isExistingPublishedDashboard ? 'Update the published dashboard' : 'Publish the dashboard to the left menu'">
          <mat-icon>{{ isExistingPublishedDashboard ? 'sync' : 'publish' }}</mat-icon> {{ publishButtonLabel }}
        </button>

        <ng-container *ngIf="!isDashboardFullscreen">
          <button type="button" class="add-step-button" mat-stroked-button (click)="startNewChart()" matTooltip="Clear the builder to start a new chart"><mat-icon>add</mat-icon> New chart</button>
          <button type="button" class="add-step-button" mat-stroked-button [disabled]="!dashboardTiles.length && !dashboardName.trim()" (click)="startNewDashboard()" matTooltip="Clear all charts and start a fresh dashboard"><mat-icon>note_add</mat-icon> New dashboard</button>
          <button type="button" class="add-step-button" mat-stroked-button (click)="openLandingPageBuilder()" matTooltip="Create a landing page from a predefined template"><mat-icon>web</mat-icon> Landing page</button>
          <button type="button" class="add-step-button" mat-stroked-button [disabled]="dashboardTiles.length < 2" [class.is-active]="groupSelectMode" (click)="toggleGroupSelectMode()" [matTooltip]="groupSelectMode ? 'Cancel selecting charts to group' : 'Select charts to club into one card'">
            <mat-icon>{{ groupSelectMode ? 'close' : 'select_all' }}</mat-icon> {{ groupSelectMode ? 'Cancel' : 'Group charts' }}
          </button>
          <button *ngIf="groupSelectMode" type="button" class="add-step-button dvm-publish-button" mat-flat-button color="primary" [disabled]="!canGroupSelectedTiles" (click)="groupSelectedTiles()" matTooltip="Club the selected charts into one titled card">
            <mat-icon>dashboard_customize</mat-icon> Group ({{ selectedTileIds.size }})
          </button>
          <!-- <button type="button" class="add-step-button" mat-stroked-button [disabled]="!dashboardTiles.length" (click)="toggleDashboardFullscreen()" matTooltip="Preview full screen">
            <mat-icon>fullscreen</mat-icon> Preview
          </button> -->
          <!-- <button type="button" class="add-step-button" mat-stroked-button [disabled]="!canSaveDashboard" (click)="saveDashboard()" matTooltip="Save this dashboard draft">
            <mat-icon>save</mat-icon> {{ isSavingDashboard ? 'Saving…' : 'Save' }}
          </button> -->
          <button type="button" class="add-step-button dvm-publish-button" mat-flat-button color="primary" [disabled]="!canPublish" (click)="publishDashboard()" [matTooltip]="isExistingPublishedDashboard ? 'Update the published dashboard' : 'Publish the dashboard to the left menu'">
            <mat-icon>{{ isExistingPublishedDashboard ? 'sync' : 'publish' }}</mat-icon> {{ publishButtonLabel }}
          </button>
          <button
            type="button"
            class="panel-collapse-button preview-collapse-button"
            [class.is-hidden-when-collapsed]="previewPanelCollapsed"
            mat-icon-button
            (click)="previewPanelCollapsed = !previewPanelCollapsed"
            [matTooltip]="previewPanelCollapsed ? 'Open dashboard' : 'Collapse dashboard'"
            [attr.aria-label]="previewPanelCollapsed ? 'Open dashboard panel' : 'Collapse dashboard panel'"
          ><mat-icon>{{ paneCollapseIcon('preview') }}</mat-icon></button>
        </ng-container>
      </div>
    </div>

    <div class="preview-panel-content panel-content">
      <div class="dvm-dashboard-canvas" #dashboardCanvas>
        <!-- Active cross-filter chip: shows what a bar-click filtered the other
             tiles by, with a Clear button to reset. -->
        <div class="dvm-crossfilter-chip" *ngIf="activeCrossFilter">
          <mat-icon>filter_alt</mat-icon>
          <span>Filtered: <strong>{{ activeCrossFilter.column }}</strong> = <strong>{{ activeCrossFilter.values.join(', ') }}</strong></span>
          <button type="button" mat-icon-button matTooltip="Clear filter" (click)="clearCrossFilter()"><mat-icon>close</mat-icon></button>
        </div>

        <div class="empty-state dvm-canvas-empty" *ngIf="!dashboardTiles.length">
          <div class="empty-state-icon"><mat-icon>dashboard</mat-icon></div>
          <p>Name your dashboard, build a chart on the left,<br />and click <strong>Generate Configuration</strong><br />to add it here. Drag a tile to reorder, drag a corner to resize.</p>
        </div>

        <!-- Reusable Format popup body (JSON-driven controls) — used for a normal
             top-level tile AND for each member inside a GROUP card, so a grouped
             chart keeps its own independent Format popup, same as before. -->
        <ng-template #formatPopup let-tile="tile">
          <div class="dvm-format-popup-head">
            <span>Format</span>
            <button type="button" mat-icon-button (click)="toggleTileFormat(tile, $event)" aria-label="Close"><mat-icon>close</mat-icon></button>
          </div>
          <div class="dvm-format-popup-body">
            <!-- Accordion sections: Title / Axis / Tooltip / Formatting / Advanced
                 (grouped by each control's `group`, JSON-driven). -->
            <div class="dvm-format-group" *ngFor="let group of formatGroupsFor(tile); let gi = index" [class.is-open]="isFormatGroupOpen(tile, group.name, gi)">
              <button type="button" class="dvm-format-group-head" (click)="toggleFormatGroup(tile, group.name, gi)">
                <span>{{ group.name }}</span>
                <mat-icon>{{ isFormatGroupOpen(tile, group.name, gi) ? 'expand_less' : 'expand_more' }}</mat-icon>
              </button>

              <div class="dvm-format-group-body" *ngIf="isFormatGroupOpen(tile, group.name, gi)">
                <ng-container *ngFor="let control of group.controls">
                <div class="dvm-format-row" [class.dvm-format-row-half]="control.width === 'half'" *ngIf="formatControlVisible(control, tile.format)">
                  <label>{{ control.label }}</label>

                  <!-- CRUD endpoints: each gets its OWN request-body editor, right
                       beside its label, so every URL can post a different contract. -->
                  <button
                    type="button"
                    class="dvm-pt-open-btn"
                    *ngIf="control.requestBodyEditor"
                    [class.is-set]="hasPayloadTemplate(tile.format, control.key)"
                    [matTooltip]="'Configure the request body posted to this endpoint'"
                    (click)="openPayloadTemplateModalForTile(tile, control, $event)"
                  >{{ hasPayloadTemplate(tile.format, control.key) ? 'Body ✓' : 'Body' }}</button>

                  <input
                    *ngIf="control.control === 'text' || control.control === 'row-filter'"
                    type="text"
                    [ngModel]="tile.format[control.key]"
                    (ngModelChange)="tile.format[control.key] = $event; onTileFormatChange(tile)"
                    [placeholder]="control.key === 'title' ? tile.title : (control.placeholder || '')"
                  />

                  <input
                    *ngIf="control.control === 'number'"
                    type="number"
                    [min]="control.min ?? null"
                    [max]="control.max ?? null"
                    [step]="control.step ?? 1"
                    [ngModel]="tile.format[control.key]"
                    (ngModelChange)="tile.format[control.key] = $event; onTileFormatChange(tile)"
                  />

                  <input
                    *ngIf="control.control === 'color'"
                    type="color"
                    [ngModel]="tile.format[control.key]"
                    (ngModelChange)="tile.format[control.key] = $event; onTileFormatChange(tile)"
                  />

                  <label *ngIf="control.control === 'toggle'" class="dvm-format-toggle">
                    <input
                      type="checkbox"
                      [ngModel]="tile.format[control.key]"
                      (ngModelChange)="tile.format[control.key] = $event; onTileFormatChange(tile)"
                    />
                  </label>

                  <ng-select
                    *ngIf="control.control === 'select'"
                    class="ss-select"
                    appendTo="body"
                    ngClass="ss-dropdown-panel"
                    [items]="formatControlOptions(control, tile)"
                    bindLabel="label"
                    bindValue="value"
                    [clearable]="true"
                    [ngModel]="tile.format[control.key]"
                    (ngModelChange)="tile.format[control.key] = $event ?? ''; onTileFormatChange(tile); openFormatTileId = null"
                  ></ng-select>

                  <ng-select
                    *ngIf="control.control === 'column-select'"
                    class="ss-select"
                    appendTo="body"
                    ngClass="ss-dropdown-panel"
                    [items]="formatControlOptions(control, tile)"
                    bindLabel="label"
                    bindValue="value"
                    [clearable]="true"
                    [ngModel]="tile.format[control.key]"
                    (ngModelChange)="tile.format[control.key] = $event || ''; onTileFormatChange(tile); openFormatTileId = null"
                  ></ng-select>
                </div>
                </ng-container>
              </div>
            </div>
          </div>
        </ng-template>

        <!-- Reusable "render this tile's actual chart" block — used for a normal
             top-level tile AND for each member inside a GROUP card, so grouping
             never needs a second copy of the per-type render logic. -->
        <ng-template #tileBody let-tile="tile">
          <!-- Waterfall -->
          <app-waterfall-chart *ngIf="tile.waterfallData" class="dvm-tile-chart" [data]="tile.waterfallData" (crossFilterChange)="onTileCrossFilter(tile, $event)"></app-waterfall-chart>

          <!-- Drill-down table -->
          <lib-drill-down *ngIf="tile.drillData" [data]="tile.drillData"></lib-drill-down>

          <!-- Nested table -->
          <app-nested-table *ngIf="tile.nestedTableData" class="dvm-tile-chart" [data]="tile.nestedTableData"></app-nested-table>

          <!-- Table & Matrix (Normal / Editable / Drill-Down) -->
          <app-unified-table-matrix-chart
            *ngIf="tile.tableMatrixData"
            class="dvm-tile-chart"
            [data]="tile.tableMatrixData"
            (crossFilterChange)="onTileCrossFilter(tile, $event)"
            (navigate)="onTileNavigate(tile, $event)"
            (refresh)="onTileTableRefresh(tile)"
            (saved)="onTileTableRefresh(tile)"
            (columnOrderChange)="onTileColumnReorder(tile, $event)"
            (save)="onTileTableSave(tile, $event)"
          ></app-unified-table-matrix-chart>

          <!-- Horizontal bar chart -->
          <app-horizontal-bar-chart *ngIf="tile.hbData" [data]="tile.hbData"></app-horizontal-bar-chart>

          <!-- Unified Bar & Column chart (Simple/Grouped/Stacked/100%/Clustered, H/V) -->
          <app-unified-bar-column-chart *ngIf="tile.barData" class="dvm-tile-chart" [data]="tile.barData" (crossFilterChange)="onTileCrossFilter(tile, $event)"></app-unified-bar-column-chart>

          <!-- Unified Line & Area chart (Line/Multi/Step/Spline; Area/Stacked/100%; analytics) -->
          <app-unified-line-area-chart *ngIf="tile.lineAreaData" class="dvm-tile-chart" [data]="tile.lineAreaData" (crossFilterChange)="onTileCrossFilter(tile, $event)"></app-unified-line-area-chart>

          <!-- Unified Scatter & Bubble chart (Scatter / Multi / Bubble / Quadrant) -->
          <app-unified-scatter-bubble-chart *ngIf="tile.scatterBubbleData" class="dvm-tile-chart" [data]="tile.scatterBubbleData" (crossFilterChange)="onTileCrossFilter(tile, $event)"></app-unified-scatter-bubble-chart>

          <!-- Unified Pie & Composition chart (Pie / Donut / Semi Donut / Sunburst / Treemap / Progress) -->
          <app-unified-pie-composition-chart *ngIf="tile.pieCompositionData" class="dvm-tile-chart" [data]="tile.pieCompositionData" (crossFilterChange)="onTileCrossFilter(tile, $event)"></app-unified-pie-composition-chart>

          <!-- KPI card -->
          <app-number-stats-shared *ngIf="tile.kpiData" [data]="tile.kpiData"></app-number-stats-shared>

          <!-- Dataset-backed dropdown input. -->
          <div class="dvm-tile-input" *ngIf="tile.dropdownControl">
            <app-global-dropdown
              [data]="tile.dropdownControl.widgetData"
              [options]="tile.dropdownControl.options"
              [ngModel]="tile.dropdownControl.value"
              (ngModelChange)="onTileDropdownChange(tile, $event)"
            ></app-global-dropdown>
            <small class="control-hint" *ngIf="tile.dropdownControl.targets.length">
              Filters {{ tile.dropdownControl.targets.length }} target(s).
            </small>
          </div>

          <!-- Input control (slider / input box) — reuses the shared widgets. -->
          <div class="dvm-tile-input" *ngIf="tile.inputControl">
            <app-mat-slider
              *ngIf="tile.inputControl.kind === 'SLIDER'"
              [data]="tile.inputControl.widgetData"
              (valueChange)="onTileSliderChange(tile, $event)"
            ></app-mat-slider>
            <app-input-box
              *ngIf="tile.inputControl.kind === 'INPUT_BOX'"
              [data]="tile.inputControl.widgetData"
              (valueChange)="onTileInputValueChange(tile, $event)"
            ></app-input-box>
            <small class="control-hint" *ngIf="tile.inputControl.targets.length">
              Drives {{ tile.inputControl.targets.length }} chart(s) with per-target formulas.
            </small>
          </div>

          <!-- File upload control — reusable uploader; the picked file POSTs to
               compute + drives targets (handled by onTileFileSelected). -->
          <div class="dvm-tile-input dvm-tile-upload" *ngIf="tile.fileUploadControl">
            <app-dvm-file-upload
              [title]="tile.title"
              [buttonLabel]="tile.fileUploadControl.buttonLabel"
              [accept]="tile.fileUploadControl.accept"
              [uploading]="isTileUploading(tile)"
              [message]="tileUploadMessage(tile)"
              [targetCount]="tile.fileUploadControl.targets.length"
              (filePicked)="onTileFilePicked(tile, $event)"
            ></app-dvm-file-upload>
          </div>

          <!-- Popup component — trigger + self-contained overlay dialog. -->
          <div class="dvm-tile-input dvm-tile-popup" *ngIf="tile.popupControl">
            <app-dvm-popup
              [data]="tile.popupControl.widgetData"
              (navigateTo)="onPopupNavigate($event)"
            ></app-dvm-popup>
          </div>
        </ng-template>

        <div
          class="dvm-dashboard-tile"
          [class.is-group-tile]="tile.layoutType === 'GROUP'"
          [ngStyle]="groupCardStyles(tile)"
          *ngFor="let tile of dashboardTiles; let i = index; trackBy: trackTileById"
          [class.is-drop-target]="tileDragIndex !== null && tileDragIndex !== i"
          [class.is-formatting]="openFormatTileId === tile.id"
          [style.--dvm-card-header-bg]="tileTakesHeaderFormat(tile) ? (tile.format?.['cardHeaderBackground'] || null) : null"
          [style.--dvm-card-header-color]="tileTakesHeaderFormat(tile) ? (tile.format?.['cardHeaderColor'] || null) : null"
          [style.--dvm-card-header-align]="tileTakesHeaderFormat(tile) ? (tile.format?.['titleAlign'] || null) : null"
          [style.--dvm-tile-font-family]="tileFontFamily(tile.format)"
          [style.--dvm-tile-font-size]="tileFontSizePx(tile.format)"
          [class.dvm-tile-no-header-format]="!tileTakesHeaderFormat(tile)"
          [class.is-selectable]="groupSelectMode && tile.layoutType !== 'GROUP'"
          [class.is-selected]="isTileSelected(tile)"
          [style.grid-column]="'span ' + tile.colSpan"
          [style.grid-row]="'span ' + tile.rowSpan"
          (dragover)="onTileReorderOver($event)"
          (drop)="onTileReorderDrop(i, $event)"
          (click)="groupSelectMode && tile.layoutType !== 'GROUP' ? toggleTileSelected(tile) : null"
        >
          <!-- Recompute overlay — shown ONLY on the tile(s) this input control
               targets while its recompute POST is in flight, not the whole canvas. -->
          <div class="dvm-recompute-overlay" *ngIf="isTileRecomputing(tile)">
            <span class="dvm-recompute-spinner" aria-hidden="true"></span>
          </div>
          <!-- Builder chrome (grip / title / type badge / format / remove) is
               hidden in Preview so the tile renders as the clean published card —
               unless "Edit Layout" is toggled on to rearrange/resize the tiles. -->
          <div
            *ngIf="!isDashboardFullscreen || previewEditLayout"
            class="dvm-tile-header"
            draggable="true"
            (dragstart)="onTileReorderStart(i, $event)"
            (dragend)="onTileReorderEnd()"
            matTooltip=""
          >
            <input
              *ngIf="groupSelectMode && tile.layoutType !== 'GROUP'"
              type="checkbox"
              class="dvm-tile-select-check"
              [checked]="isTileSelected(tile)"
              (click)="$event.stopPropagation()"
              (change)="toggleTileSelected(tile)"
              aria-label="Select chart to group"
            />
            <span *ngIf="tile.layoutType !== 'GROUP'" class="dvm-tile-title" [title]="tile.title | capitalCase">{{ tile.title | capitalCase }}</span>
            <input
              *ngIf="tile.layoutType === 'GROUP'"
              type="text"
              class="dvm-tile-title dvm-group-title-input"
              [(ngModel)]="tile.groupTitle"
              (click)="$event.stopPropagation()"
              placeholder="Group card title"
            />
            <!-- Actions overlay the right edge and appear on tile hover, so the
                 title keeps the full header width on narrow tiles. -->
            <div class="dvm-tile-actions">
              <button
                *ngIf="tile.layoutType !== 'GROUP'"
                type="button"
                mat-icon-button
                class="dvm-tile-edit"
                [class.is-active]="editingTileId === tile.id"
                (click)="editTile(tile, $event)"
                matTooltip="Edit chart configuration"
                aria-label="Edit chart configuration"
              >
                <mat-icon>edit</mat-icon>
              </button>
            <!-- Cross-filter listener editor — publishes a filter key + picks which
                 tiles react. Shown for any registered client-side chart tile
                 (registry-driven, so Pie/Line/… get it automatically) plus
                 source-only publishers like the waterfall. -->
            <button
              *ngIf="canConfigureCrossFilter(tile)"
              type="button"
              mat-icon-button
              class="dvm-tile-filter"
              [class.is-active]="expandedListenerTileId === tile.id"
              (click)="toggleListenerEditor(tile, $event)"
              matTooltip="Cross-filter (publishes filter key)"
              aria-label="Cross-filter settings"
            >
              <mat-icon>filter_alt</mat-icon>
            </button>
              <button
                *ngIf="isFormattableTile(tile)"
                type="button"
                mat-icon-button
                class="dvm-tile-format"
                [class.is-active]="openFormatTileId === tile.id"
                (click)="toggleTileFormat(tile, $event)"
                matTooltip="Format chart"
                aria-label="Format chart"
              >
                <mat-icon>tune</mat-icon>
              </button>
              <button
                *ngIf="canEditInteractions(tile)"
                type="button"
                mat-icon-button
                class="dvm-tile-interactions"
                [class.is-active]="openInteractionTileId === tile.id"
                (click)="toggleTileInteractions(tile, $event)"
                matTooltip="Chart interactions (targets & references)"
                aria-label="Edit chart interactions"
              >
                <mat-icon>link</mat-icon>
              </button>
              <button *ngIf="tile.layoutType === 'GROUP'" type="button" mat-icon-button class="dvm-tile-columns" (click)="toggleGroupColumns(tile); $event.stopPropagation()" [matTooltip]="(tile.groupColumns || 1) >= 2 ? 'Switch to 1 column' : 'Switch to 2 columns'" aria-label="Toggle group columns">
                <mat-icon>{{ (tile.groupColumns || 1) >= 2 ? 'view_agenda' : 'view_column' }}</mat-icon>
              </button>
              <button *ngIf="tile.layoutType === 'GROUP'" type="button" mat-icon-button class="dvm-tile-ungroup" (click)="ungroupTile(tile); $event.stopPropagation()" matTooltip="Ungroup — restore as separate charts" aria-label="Ungroup">
                <mat-icon>call_split</mat-icon>
              </button>
              <button type="button" mat-icon-button class="dvm-tile-remove" (click)="removeTile(tile)" matTooltip="Remove from dashboard" aria-label="Remove tile">
                <mat-icon>close</mat-icon>
              </button>
            </div>
          </div>

          <!-- Per-tile Format popup (JSON-driven controls). -->
          <div class="dvm-tile-format-popup" [class.align-left]="formatPopupAlign === 'left'" *ngIf="openFormatTileId === tile.id && tile.format">
            <ng-container *ngTemplateOutlet="formatPopup; context: { tile: tile }"></ng-container>
          </div>

          <!-- Cross-filter listener editor (mirrors the dashboard-builder): the
               column this tile PUBLISHES on click + which tiles REACT to it. -->
          <div class="dvm-listener-editor" *ngIf="expandedListenerTileId === tile.id">
            <div class="dvm-listener-row">
              <label>Publishes filter key</label>
              <ng-select
                #ssTileFilterKey
                class="ss-select"
                [class.is-open]="!!ssTileFilterKey.isOpen()"
                appendTo="body"
                ngClass="ss-dropdown-panel"
                [items]="tileFilterColumns(tile)"
                [clearable]="true"
                [searchable]="true"
                placeholder="(defaults to the category column)"
                [ngModel]="effectiveTileFilterKey(tile) || null"
                (ngModelChange)="setTileFilterKey(tile, $event || '')"
                (clear)="setTileFilterKey(tile, '')"
              ></ng-select>
            </div>
            <div class="dvm-listener-row">
              <label>Components that react to this card</label>
              <div class="dvm-listener-checkboxes" *ngIf="reactableTilesFor(tile).length; else noOtherTiles">
                <label class="dvm-listener-checkbox" *ngFor="let target of reactableTilesFor(tile)">
                  <input
                    type="checkbox"
                    [checked]="isTileListener(tile, target.id)"
                    (change)="toggleTileListener(tile, target.id)"
                  />
                  <span>{{ target.title || target.layoutType }} <small>({{ target.layoutType | lowercase }})</small></span>
                </label>
              </div>
              <ng-template #noOtherTiles>
                <small class="control-hint">Add more charts to wire cross-filter listeners.</small>
              </ng-template>
            </div>
            <div class="dvm-listener-actions">
              <button type="button" class="dvm-listener-reset" (click)="resetTileListeners(tile)">Reset to chart defaults</button>
              <button type="button" class="dvm-listener-done" (click)="toggleListenerEditor(tile)">Done</button>
            </div>
          </div>

          <div class="dvm-tile-body" *ngIf="tile.layoutType !== 'GROUP'">
            <ng-container *ngTemplateOutlet="tileBody; context: { tile: tile }"></ng-container>
          </div>

          <!-- GROUP card: members stack inside, each keeping its OWN render AND
               its own Edit/Format/Interactions — grouping only changes WHERE a
               chart renders, not what you can still do to it individually. -->
          <div class="dvm-group-body" [class.two-col]="(tile.groupColumns || 1) >= 2" *ngIf="tile.layoutType === 'GROUP'">
            <div class="dvm-group-empty" *ngIf="!tile.groupMembers?.length">Empty group — ungroup or remove it.</div>
            <div class="dvm-group-member" [class.is-formatting]="openFormatTileId === member.id" *ngFor="let member of tile.groupMembers" [style.--dvm-card-header-align]="member.format?.['titleAlign'] || null">
              <div class="dvm-group-member-head">
                <span [title]="member.title">{{ member.title }}</span>
                <div class="dvm-group-member-actions">
                  <!-- Order inside the card IS the layout: members render (and
                       publish) in this sequence, so these move the chart within
                       the group. -->
                  <button type="button" mat-icon-button class="dvm-group-member-move"
                          [disabled]="!canMoveGroupMember(tile, member, -1)"
                          (click)="moveGroupMember(tile, member, -1, $event)"
                          matTooltip="Move earlier in the card" aria-label="Move earlier in the card">
                    <mat-icon>arrow_upward</mat-icon>
                  </button>
                  <button type="button" mat-icon-button class="dvm-group-member-move"
                          [disabled]="!canMoveGroupMember(tile, member, 1)"
                          (click)="moveGroupMember(tile, member, 1, $event)"
                          matTooltip="Move later in the card" aria-label="Move later in the card">
                    <mat-icon>arrow_downward</mat-icon>
                  </button>
                  <button type="button" mat-icon-button [class.is-active]="editingTileId === member.id" (click)="editTile(member, $event)" matTooltip="Edit chart configuration" aria-label="Edit chart configuration">
                    <mat-icon>edit</mat-icon>
                  </button>
                  <button *ngIf="isFormattableTile(member)" type="button" mat-icon-button [class.is-active]="openFormatTileId === member.id" (click)="toggleTileFormat(member, $event)" matTooltip="Format chart" aria-label="Format chart">
                    <mat-icon>tune</mat-icon>
                  </button>
                  <button *ngIf="canEditInteractions(member)" type="button" mat-icon-button [class.is-active]="openInteractionTileId === member.id" (click)="toggleTileInteractions(member, $event)" matTooltip="Chart interactions (targets & references)" aria-label="Edit chart interactions">
                    <mat-icon>link</mat-icon>
                  </button>
                  <button type="button" mat-icon-button (click)="removeGroupMember(tile, member)" matTooltip="Remove from group" aria-label="Remove from group">
                    <mat-icon>close</mat-icon>
                  </button>
                </div>
              </div>
              <div class="dvm-tile-format-popup dvm-group-member-format-popup" [class.align-left]="formatPopupAlign === 'left'" *ngIf="openFormatTileId === member.id && member.format">
                <ng-container *ngTemplateOutlet="formatPopup; context: { tile: member }"></ng-container>
              </div>
              <div class="dvm-tile-body dvm-group-member-body">
                <ng-container *ngTemplateOutlet="tileBody; context: { tile: member }"></ng-container>
              </div>
            </div>
          </div>

          <span *ngIf="!isDashboardFullscreen || previewEditLayout" class="dvm-tile-resize" (mousedown)="startTileResize(tile, $event)" matTooltip="Drag to resize" aria-hidden="true"></span>
        </div>
      </div>
    </div>
  </div>

  <div class="pane-resizer pane-resizer--data" [class.is-disabled]="isDividerDisabled(1)" (mousedown)="startDividerResize(1, $event)" aria-hidden="true"></div>

  <aside class="dvm-layout-data-panel wf-section" [class.is-collapsed]="dataPanelCollapsed" [style.grid-column]="paneGridColumn('data')" (dragover)="onPaneDragOver($event)" (drop)="onPaneDrop('data', $event)">
    <header class="wf-section-header data-panel-header pane-drag-handle" draggable="true" (dragstart)="onPaneDragStart('data', $event)" (dragend)="onPaneDragEnd()" matTooltip="Drag to reorder panel">
      <div><span class="wf-section-kicker">Data explorer</span><h3>Tables &amp; fields</h3></div>
      <div class="wf-header-actions">
        <mat-icon matTooltip="Drag a field onto a bridge step">dataset</mat-icon>
        <button
          type="button"
          class="panel-collapse-button"
          mat-icon-button
          (click)="dataPanelCollapsed = !dataPanelCollapsed"
          [matTooltip]="dataPanelCollapsed ? 'Open data' : 'Collapse data'"
          [attr.aria-label]="dataPanelCollapsed ? 'Open data panel' : 'Collapse data panel'"
        ><mat-icon>{{ paneCollapseIcon('data') }}</mat-icon></button>
      </div>
    </header>

    <div class="data-panel-content panel-content">
    <div class="data-search">
      <mat-icon>search</mat-icon>
      <input type="search" [(ngModel)]="dataPaneFilter" placeholder="Search tables" aria-label="Search tables" />
    </div>

    <div class="data-browser">
      <div class="data-table-list" aria-label="Available tables">
        <button
          type="button"
          class="data-table-item"
          *ngFor="let entity of filteredExplorerEntities"
          [class.is-active]="entity === activeExplorerEntity"
          (click)="selectExplorerEntity(entity)"
          [title]="entity"
        >
          <mat-icon>table_chart</mat-icon><span>{{ entity }}</span><mat-icon>chevron_right</mat-icon>
        </button>
        <div class="data-pane-empty" *ngIf="!entitiesLoading && !filteredExplorerEntities.length">No tables found</div>
        <div class="data-pane-empty" *ngIf="entitiesLoading">Loading tables…</div>
      </div>

      <div class="data-field-list" aria-label="Table fields">
        <div class="data-field-heading" *ngIf="activeExplorerEntity" [title]="activeExplorerEntity">{{ activeExplorerEntity }}</div>
        <div class="field-search" *ngIf="activeExplorerEntity && !explorerColumnsLoading">
          <mat-icon>search</mat-icon>
          <input type="search" [(ngModel)]="fieldPaneFilter" placeholder="Search columns" aria-label="Search columns" />
          <button type="button" *ngIf="fieldPaneFilter" (click)="fieldPaneFilter = ''" aria-label="Clear column search">
            <mat-icon>close</mat-icon>
          </button>
        </div>
        <div class="data-field-scroll">
          <div class="data-pane-empty" *ngIf="!activeExplorerEntity">Select a table to view its fields.</div>
          <div class="data-pane-empty" *ngIf="explorerColumnsLoading">Loading fields…</div>
          <div class="data-pane-empty" *ngIf="activeExplorerEntity && !explorerColumnsLoading && !filteredExplorerColumns.length">
            {{ fieldPaneFilter ? 'No matching columns' : 'No columns available' }}
          </div>
          <div
            class="data-field-item"
            *ngFor="let column of filteredExplorerColumns"
            draggable="true"
            (dragstart)="onExplorerFieldDragStart($event, activeExplorerEntity, column)"
            [title]="'Drag ' + column + ' to a bridge step'"
          >
            <mat-icon>drag_indicator</mat-icon><span>{{ column }}</span>
          </div>
        </div>
      </div>
    </div>
    </div>
  </aside>
</section>

<!-- Tile "Interactions" overlay and the table "Request body" popup — own components
     (tile-interactions-modal / payload-template-modal), rendered at the template
     ROOT so they are real centered overlays, never clipped by a tile card. -->
<app-tile-interactions-modal
  *ngIf="interactionTile"
  [tile]="interactionTile"
  (save)="onInteractionsSaved($event)"
  (cancel)="closeTileInteractions()"
></app-tile-interactions-modal>
<app-payload-template-modal
  *ngIf="payloadTemplateModal"
  [request]="payloadTemplateModal"
  (save)="onPayloadTemplateSaved($event)"
  (cancel)="closePayloadTemplateModal()"
></app-payload-template-modal>
